import { useEffect, useMemo, useRef, useState } from 'react';

import { Alert, Button, FormField, Icon, Input, Switch, Tag } from '../ds';
import { icons } from '../icons';
import {
  api,
  type VibeCheckProposal,
  type AppRuntime,
  type AppServiceSelection,
  type ScaffoldContext,
  type ScaffoldResult,
  type ScaffoldStep,
} from '../api';
import { c, cardStyle, h1Style, ledeStyle, mono, pageStyle } from '../tokens';
import './wizard.css';
import './vibes-v2.css';
import { APP_TYPES } from '../appTypes';
import {
  REPO_PREFIX,
  RepoNameField,
  suffixError,
  suffixFromAppName,
} from '../components/RepoNameField';
import {
  AppServicePicker,
  DEFAULT_SCHEDULER_CRON,
  NO_APP_SERVICES,
  schedulerCronError,
  selectedServiceLabels,
} from '../components/AppServicePicker';

const STEP_LABELS = [
  'Your app',
  'What it needs',
  'Where it lives',
  'Connect GitHub',
];

interface CreateWizardProps {
  templateId: string;
  initialProposal?: VibeCheckProposal;
  onBack: () => void;
  onVibeCheck?: () => void;
  onRestart?: () => void;
  previewOnly?: boolean;
  onCreated: (repo: string) => void;
}

export function CreateWizard({ templateId, initialProposal, onBack, onCreated, onVibeCheck, onRestart, previewOnly = false }: CreateWizardProps) {
  const [step, setStep] = useState(1);
  const [appType, setAppType] = useState<string>('Dashboard');
  const [previewSubmitted, setPreviewSubmitted] = useState(false);

  const [appName, setAppName] = useState(initialProposal?.appName ?? '');
  const [description, setDescription] = useState(initialProposal?.description ?? '');
  const [scaffoldContext, setScaffoldContext] = useState<ScaffoldContext | null>(null);
  const [runtime, setRuntime] = useState<AppRuntime>(initialProposal?.runtime ?? 'typescript');
  const [secrets, setSecrets] = useState(initialProposal?.enableSecretsManagement ?? false);
  const [devOnly, setDevOnly] = useState(false);
  const [environmentModeReason, setEnvironmentModeReason] = useState('');
  const [environmentModeConfirmation, setEnvironmentModeConfirmation] = useState('');
  const [services, setServices] = useState<AppServiceSelection>(initialProposal?.services ?? NO_APP_SERVICES);
  const [schedulerCron, setSchedulerCron] = useState(initialProposal?.schedulerCron ?? DEFAULT_SCHEDULER_CRON);
  const [isPublic, setIsPublic] = useState(true);
  /**
   * Only the part AFTER `cdp-` is editable, and it stays null until the user
   * overrides it so the derived slug keeps tracking the app name.
   */
  const [repoSuffixEdited, setRepoSuffixEdited] = useState<string | null>(null);

  const [nameCheck, setNameCheck] = useState<{
    state: 'idle' | 'checking' | 'available' | 'taken' | 'error';
    reason?: string;
  }>({ state: 'idle' });

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ScaffoldResult | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  /** Non-null from the moment submit is pressed — this is what drives the live view. */
  const [liveSteps, setLiveSteps] = useState<ScaffoldStep[] | null>(null);

  const repoSuffix = repoSuffixEdited ?? suffixFromAppName(appName);
  const repoName = `${REPO_PREFIX}${repoSuffix}`;
  const localSuffixError = suffixError(repoSuffix);

  useEffect(() => {
    if (previewOnly) return;
    api
      .scaffoldContext()
      .then(({ context }) => {
        setScaffoldContext(context);
        if (!initialProposal) setRuntime(context.defaults.runtime);
      })
      .catch(() => setScaffoldContext(null));
  }, [initialProposal, previewOnly]);

  /* Live repo-name availability check, debounced — the inline check the mock
     shows as "Available — github.com/Abridge-Commercial/<name>". */
  const checkTimer = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (previewOnly || step !== 3) return;
    window.clearTimeout(checkTimer.current);

    if (localSuffixError) {
      setNameCheck({ state: 'error', reason: localSuffixError });
      return;
    }

    setNameCheck({ state: 'checking' });
    checkTimer.current = window.setTimeout(() => {
      api
        .checkRepoName(repoName)
        .then(({ available, reason }) =>
          setNameCheck(available ? { state: 'available' } : { state: 'taken', reason }),
        )
        .catch(() => setNameCheck({ state: 'error', reason: 'Could not check availability.' }));
    }, 400);

    return () => window.clearTimeout(checkTimer.current);
  }, [repoName, localSuffixError, step, previewOnly]);

  const stepValid = useMemo(() => {
    if (step === 1) return appName.trim().length > 0 && description.trim().length > 0;
    if (step === 3) return (previewOnly ? !localSuffixError : nameCheck.state === 'available')
      && (!devOnly || (environmentModeReason.trim().length >= 20 && environmentModeReason.trim().length <= 2000 && environmentModeConfirmation === repoName));
    if (step === 2 && services.cloudScheduler) return !schedulerCronError(schedulerCron);
    // Choosing no extra services is a legitimate answer.
    return true;
  }, [step, appName, description, nameCheck.state, services.cloudScheduler, schedulerCron, previewOnly, localSuffixError, devOnly, environmentModeReason, environmentModeConfirmation, repoName]);

  const submit = () => {
    if (previewOnly) { setPreviewSubmitted(true); return; }
    setSubmitting(true);
    setSubmitError(null);
    // Switch to the live view before the first byte comes back, so the transition
    // happens on the click rather than on the first server response.
    setLiveSteps([]);

    api
      .scaffold(
        {
          templateId,
          appName: appName.trim(),
          appType,
          description: description.trim(),
          repoName,
          runtime,
          enableSecretsManagement: secrets,
          devOnly: scaffoldContext?.isCdpAdmin === true && devOnly,
          ...(devOnly ? { environmentModeReason, environmentModeConfirmation } : {}),
          services,
          schedulerCron,
          isPublic,
        },
        setLiveSteps,
      )
      // No auto-redirect on success. The completed step list carries the two links
      // worth having — the new repo and the infra PR — so navigating away on a
      // timer would destroy the thing the user is reading. Leaving is their call.
      .then(setResult)
      .catch((error: unknown) => {
        setSubmitError(error instanceof Error ? error.message : 'Could not create the app.');
        setLiveSteps(null); // fall back to the form, with the answers intact
      })
      .finally(() => setSubmitting(false));
  };

  const steps = STEP_LABELS.map((label, index) => {
    const n = index + 1;
    const active = n === step;
    const done = n < step;
    return {
      key: label,
      n: done ? '✓' : String(n),
      label,
      underline: active ? c.brand : 'transparent',
      dotBg: active ? c.brand : done ? c.teal : c.border,
      dotFg: active || done ? '#FFFFFF' : c.textFaint,
      fg: active ? c.text : c.textMuted,
    };
  });

  /* From the moment submit is pressed, the step list IS the screen — it starts as
     the pending plan and fills in as the server reports each transition. */
  if (liveSteps) {
    const running = !result;
    const createdAnything = liveSteps.some(
      step => step.id === 'publish' && step.status === 'ok',
    );
    const currentStep = liveSteps.find(step => step.status === 'running');

    const heading = running
      ? 'Setting up your app'
      : result?.ok
        ? 'Your app is ready'
        : 'Setup stopped partway';

    const lede = running
      ? (currentStep?.name ?? 'Starting…')
      : result?.ok
        ? 'Your app is set up and its infrastructure is awaiting approval. Nothing else is needed from you.'
        : createdAnything
          ? 'Some steps did not complete. Anything already created is listed below — a Vibes admin can finish the rest.'
          : 'Nothing was created, so this is safe to retry. Your answers are still filled in.';

    const done = liveSteps.filter(step => step.status !== 'pending' && step.status !== 'running');

    return (
      <div className="v2-page" style={{ ...pageStyle, gap: 24 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <h1 style={h1Style}>{heading}</h1>
          <p style={ledeStyle} aria-live="polite">
            {lede}
          </p>
        </div>

        {/* Determinate progress: the plan is known upfront, so this is honest
            rather than a spinner pretending to know how long things take. */}
        {running && liveSteps.length > 0 && (
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={liveSteps.length}
            aria-valuenow={done.length}
            style={{
              height: 4,
              borderRadius: 999,
              background: c.border,
              overflow: 'hidden',
              maxWidth: 560,
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${(done.length / liveSteps.length) * 100}%`,
                background: c.brand,
                transition: 'width 240ms ease-out',
              }}
            />
          </div>
        )}

        <div
          style={{ ...cardStyle, padding: 24, display: 'flex', flexDirection: 'column', gap: 4 }}
        >
          {liveSteps.length === 0 && (
            <span style={{ fontSize: 13.5, color: c.textMuted }}>Starting…</span>
          )}

          {liveSteps.map((scaffoldStep, index) => (
            <div
              key={scaffoldStep.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '22px 1fr',
                gap: 12,
                alignItems: 'start',
                padding: '10px 0',
                borderBottom:
                  index === liveSteps.length - 1 ? 'none' : `1px solid ${c.border}`,
                // Steps not reached yet recede, so the eye lands on the active one.
                opacity: scaffoldStep.status === 'pending' ? 0.45 : 1,
                transition: 'opacity 200ms ease-out',
              }}
            >
              <StepBullet status={scaffoldStep.status} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 475,
                    letterSpacing: '0.28px',
                    color: scaffoldStep.status === 'error' ? '#B3261E' : c.text,
                  }}
                >
                  {scaffoldStep.name}
                </span>
                {scaffoldStep.detail && (
                  <span style={{ fontSize: 12.5, lineHeight: 1.45, color: c.textMuted }}>
                    {scaffoldStep.detail}
                  </span>
                )}
                {scaffoldStep.url && scaffoldStep.id !== 'infra-pr' && (
                  <a
                    href={scaffoldStep.url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: 12.5 }}
                  >
                    {scaffoldStep.url}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        {result && !result.ok && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Button
              variant="primary"
              onPress={() => {
                // Back to the review step with every answer intact.
                setResult(null);
                setLiveSteps(null);
                setSubmitError(null);
                setStep(4);
              }}
            >
              Back to the form
            </Button>
            <span style={{ fontSize: 13, color: c.textMuted }}>
              Your answers are preserved — nothing needs re-typing.
            </span>
          </div>
        )}

        {result?.ok && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="primary"
              rightIcon={icons.arrowRight}
              onPress={() => onCreated(repoName)}
            >
              View your app
            </Button>
            <Button variant="tertiary" onPress={onRestart ?? onBack}>
              Create another
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (previewSubmitted) return <section style={{ ...pageStyle, gap: 20 }}><div className="v2-card"><h1 style={h1Style}>Your app is on its way</h1><p>This is a preview of the submission confirmation for {appName}. In the signed-in portal, the setup is sent to a Vibes admin for review.</p><p>No repository or cloud resources were created in this preview.</p><div className="v2-row"><button type="button" className="v2-pill primary" onClick={() => onCreated(repoName)}>Go to the Catalog</button><button type="button" className="v2-pill" onClick={() => setPreviewSubmitted(false)}>Back to review</button></div></div></section>;
  return (
    <div className="v2-page" style={{ ...pageStyle, gap: 24 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h1 style={h1Style}>Create an app</h1>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: c.textFaint }}>
            Start a new app. Vibes will handle security and setup behind the scenes.
          </p>
        </div>
      </div>

      {submitError && (
        <Alert variant="danger" showIcon>
          <span style={{ fontSize: 14, lineHeight: 1.35 }}>{submitError}</span>
        </Alert>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(460px, 100%), 1fr))',
          gap: 28,
          alignItems: 'start',
        }}
      >
        <div style={{ ...cardStyle, overflow: 'hidden' }}>
          <div
            style={{
              display: 'flex',
              gap: 0,
              flexWrap: 'wrap',
              borderBottom: `1px solid ${c.border}`,
              background: c.subtle,
            }}
          >
            {steps.map(s => (
              <div
                key={s.key}
                style={{
                  flex: '1 1 180px',
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '16px 20px',
                  borderBottom: `2px solid ${s.underline}`,
                }}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 999,
                    flex: '0 0 22px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 475,
                    background: s.dotBg,
                    color: s.dotFg,
                  }}
                >
                  {s.n}
                </div>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 475,
                    letterSpacing: '0.26px',
                    color: s.fg,
                  }}
                >
                  {s.label}
                </span>
              </div>
            ))}
          </div>

          <div style={{ padding: 28 }}>
            {step === 1 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
                <FormField
                  label="App name"
                  required
                  helperText="Shown as the catalog title and the README header. Letters, numbers, spaces and hyphens only."
                >
                  <Input
                    value={appName}
                    onChange={event => setAppName(event.target.value)}
                    placeholder="Visit Summary Exporter"
                    autoFocus
                  />
                </FormField>

                <FormField label="Category" helperText="Shown in the catalog so people can filter by category.">
                  <select aria-label="Category" value={appType} onChange={event => setAppType(event.target.value)} style={{ width: '100%', font: 'inherit', fontSize: 14, padding: '11px 14px', border: `1px solid ${c.border}`, borderRadius: 10, background: c.surface }}>{APP_TYPES.map(type => <option key={type}>{type}</option>)}</select>
                </FormField>
                <FormField
                  label="Language"
                  required
                  helperText="Not sure? Keep TypeScript. It works for most apps. Choose Python if your app is mostly data work or you already know Python."
                >
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {(scaffoldContext?.runtimes ?? [
                      { id: 'typescript' as const, label: 'TypeScript' },
                      { id: 'python' as const, label: 'Python' },
                    ]).map(option => (
                      <Tag
                        key={option.id}
                        variant="default"
                        style={{ fontSize: 13 }}
                        selected={runtime === option.id}
                        onClick={() => setRuntime(option.id)}
                        aria-label={`Use ${option.label}`}
                      >
                        {option.label}
                      </Tag>
                    ))}
                  </div>
                </FormField>

                <FormField
                  label="What does it do?"
                  required
                  helperText="One line describing the app — shown in the catalog."
                >
                  <Input
                    value={description}
                    onChange={event => setDescription(event.target.value)}
                    placeholder="Exports nightly visit summaries to the analytics bucket"
                  />
                </FormField>

                <div style={{ height: 1, background: c.border }} />

                <ToggleRow
                  title="Make this app private?"
                  detail="Apps are public by default and available to all Abridge users. Turn on to make this app private. Only contributors can edit it or manage its settings."
                  checked={!isPublic}
                  onChange={isPrivate => setIsPublic(!isPrivate)}
                  ariaLabel="Make app private"
                />
              </div>
            )}

            {step === 2 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <p
                  style={{
                    margin: 0,
                    fontSize: 13.5,
                    lineHeight: 1.5,
                    color: c.textBody,
                    maxWidth: '62ch',
                    textWrap: 'pretty',
                  }}
                >
                  Hosting, builds, and an app URL are included. Choose any extras you need, or leave
                  them all off. Adding capabilities later requires admin approval through the portal.
                </p>

                <div style={{ height: 1, background: c.border }} />

                <AppServicePicker
                  helpPresentation="guide"
                  value={services}
                  onChange={setServices}
                  secretsManagement={secrets}
                  onSecretsManagementChange={setSecrets}
                  schedulerCron={schedulerCron}
                  onSchedulerCronChange={setSchedulerCron}
                />
                {scaffoldContext?.isCdpAdmin && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, border: `1px solid ${c.border}`, borderRadius: 12, padding: 16 }}>
                    <div>
                      <strong>Dev-only application</strong>
                      <p style={{ margin: '6px 0 0', color: c.textBody, fontSize: 13 }}>
                        Admin-only: do not create Prod infrastructure or offer promotion. The infrastructure PR still needs approval.
                      </p>
                    </div>
                    <Switch checked={devOnly} onCheckedChange={setDevOnly} aria-label="Dev-only application" />
                  </div>
                )}
              </div>
            )}

            {step === 3 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
                <FormField
                  label="Your app’s home on GitHub"
                  required
                  helperText={`Derived from your app name. Every portal-created repo starts with ${REPO_PREFIX} — edit the rest. A new private repository is created under Abridge-Commercial.`}
                  error={
                    nameCheck.state === 'taken' || nameCheck.state === 'error'
                      ? nameCheck.reason
                      : undefined
                  }
                >
                  <RepoNameField
                    suffix={repoSuffix}
                    onSuffixChange={setRepoSuffixEdited}
                    invalid={nameCheck.state === 'taken' || nameCheck.state === 'error'}
                  />
                </FormField>

                {devOnly && scaffoldContext?.isCdpAdmin && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <label htmlFor="new-app-mode-reason">Why does this app only need Dev?</label>
                    <textarea id="new-app-mode-reason" value={environmentModeReason} onChange={event => setEnvironmentModeReason(event.target.value)} maxLength={2000} rows={3} placeholder="Provide a reason for the infrastructure audit (at least 20 characters)." />
                    <label htmlFor="new-app-mode-confirm">Type {repoName} to confirm</label>
                    <Input id="new-app-mode-confirm" value={environmentModeConfirmation} onChange={event => setEnvironmentModeConfirmation(event.target.value)} placeholder={repoName} />
                  </div>
                )}

                {nameCheck.state === 'available' && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '14px 16px',
                      background: c.subtle,
                      border: `1px solid ${c.border}`,
                      borderRadius: 12,
                    }}
                  >
                    <Icon name={icons.available} size="sm" color={c.success} />
                    <span style={{ fontSize: 13, color: c.textBody }}>
                      Available —{' '}
                      <span style={{ fontFamily: mono }}>
                        github.com/Abridge-Commercial/{repoName}
                      </span>
                    </span>
                  </div>
                )}

                {nameCheck.state === 'checking' && (
                  <span style={{ fontSize: 13, color: c.textMuted }}>Checking availability…</span>
                )}
              </div>
            )}

            {step === 4 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
                <FormField
                  label="GitHub organization"
                  required
                  helperText="New repositories are always created in this organization."
                >
                  <Input value="Abridge-Commercial" disabled />
                </FormField>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                    padding: 18,
                    border: `1px solid ${c.border}`,
                    borderRadius: 12,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Icon name={icons.identity} size="sm" color={c.teal} />
                    <span style={{ fontSize: 15, fontWeight: 475, letterSpacing: '0.28px' }}>
                      {previewOnly ? 'GitHub connection preview' : 'Connected through your GitHub sign-in'}
                    </span>
                  </div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 13,
                      lineHeight: 1.5,
                      color: c.textBody,
                      textWrap: 'pretty',
                    }}
                  >
                    The portal creates your repository and initial files using its managed GitHub
                    identity. Your sign-in identifies you as the creator and grants you maintainer
                    access. Your app&rsquo;s infrastructure is reviewed separately before it is deployed.
                  </p>
                </div>

                <Summary
                  appName={appName}
                  appType={appType}
                  description={description}
                  repoName={repoName}
                  runtime={runtime}
                  secrets={secrets}
                  services={services}
                  schedulerCron={schedulerCron}
                  isPublic={isPublic}
                />
              </div>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              padding: '18px 28px',
              borderTop: `1px solid ${c.border}`,
              background: c.canvas,
            }}
          >
            <Button
              variant="tertiary"
              onPress={() => (step === 1 ? onBack() : setStep(step - 1))}
              disabled={submitting}
            >
              Back
            </Button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 13, color: c.textMuted }}>Step {step} of 4</span>
              <Button
                variant="primary"
                rightIcon={step === 4 ? undefined : icons.arrowRight}
                disabled={!stepValid || submitting}
                loading={submitting}
                onPress={() => (step === 4 ? submit() : setStep(step + 1))}
              >
                {step === 4 ? 'Create app' : 'Continue'}
              </Button>
            </div>
          </div>
        </div>


      </div>
      {onVibeCheck && <p style={{ margin: 0, alignSelf: 'flex-start', padding: '14px 20px', borderRadius: 18, background: c.tealBg, border: `1px solid ${c.border}`, fontSize: 15, lineHeight: 1.5, color: c.textBody }}>
        Unsure about your app? <a href="#/vibe-check" style={{ color: c.teal, textDecoration: 'underline', textUnderlineOffset: 3 }} onClick={event => { event.preventDefault(); onVibeCheck(); }}>Give it a Vibe Check.</a>
      </p>}
      {previewOnly && <p style={{ margin: 0, fontSize: 13, color: c.textMuted }}>Local preview: repository availability is not checked. Create app shows a simulated confirmation.</p>}
    </div>
  );
}

function ToggleRow({
  title,
  detail,
  checked,
  onChange,
  ariaLabel,
  disabled,
  disabledNote,
}: {
  title: string;
  detail: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  ariaLabel: string;
  disabled?: boolean;
  /** Shown in place of the normal help text, so a dead control explains itself. */
  disabledNote?: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 24,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span
          style={{
            fontSize: 15,
            fontWeight: 475,
            letterSpacing: '0.28px',
            color: disabled ? c.textMuted : c.text,
          }}
        >
          {title}
        </span>
        <span
          style={{
            fontSize: 13,
            lineHeight: 1.4,
            color: disabled ? c.textFaint : c.textMuted,
            maxWidth: '52ch',
            textWrap: 'pretty',
          }}
        >
          {disabled ? (disabledNote ?? detail) : detail}
        </span>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        aria-label={ariaLabel}
        disabled={disabled}
      />
    </div>
  );
}

function Summary(props: {
  appName: string;
  appType: string;
  description: string;
  repoName: string;
  runtime: AppRuntime;
  secrets: boolean;
  services: AppServiceSelection;
  schedulerCron: string;
  isPublic: boolean;
}) {
  const chosenServices = selectedServiceLabels(props.services);

  const rows: [string, string][] = [
    ['App name', props.appName],
    ['Category', props.appType],
    ['Description', props.description],
    ['Repository', `Abridge-Commercial/${props.repoName}`],
    ['Runtime', props.runtime === 'python' ? 'Python' : 'TypeScript'],
    ['Secret Manager', props.secrets ? 'Enabled' : 'Not enabled'],
    // Named rather than counted: the point of the row is to catch a service the
    // user did not mean to switch on.
    [
      'Google Cloud services',
      chosenServices.length > 0 ? chosenServices.join(', ') : 'None requested',
    ],
    ...(props.services.cloudScheduler
      ? [['Schedule', `${props.schedulerCron} (Pacific Time, San Francisco)`] as [string, string]]
      : []),
    ['Catalog visibility', props.isPublic ? 'Public' : 'Private'],
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <span style={{ fontSize: 13, fontWeight: 475, color: c.textMuted }}>Review</span>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '150px 1fr',
          gap: '10px 20px',
          fontSize: 13.5,
        }}
      >
        {rows.map(([label, value]) => (
          <div key={label} style={{ display: 'contents' }}>
            <span style={{ color: c.textMuted }}>{label}</span>
            <span style={{ wordBreak: 'break-word' }}>{value || '—'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepBullet({ status }: { status: string }) {
  const map: Record<string, { bg: string; fg: string; glyph: string }> = {
    ok: { bg: c.teal, fg: '#fff', glyph: '\u2713' },
    error: { bg: '#B3261E', fg: '#fff', glyph: '!' },
    running: { bg: c.brand, fg: '#fff', glyph: '\u00b7' },
    skipped: { bg: c.border, fg: c.textFaint, glyph: '\u2013' },
    pending: { bg: 'transparent', fg: c.textFaint, glyph: '\u00b7' },
  };
  const style = map[status] ?? map.pending;

  return (
    <div
      className={status === 'running' ? 'cdp-step-running' : undefined}
      style={{
        width: 22,
        height: 22,
        borderRadius: 999,
        flex: '0 0 22px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        fontWeight: 475,
        background: style.bg,
        color: style.fg,
        // Pending steps read as an empty slot waiting to be filled, rather than a
        // filled chip that looks finished.
        border: status === 'pending' ? `1px solid ${c.border}` : '1px solid transparent',
        transition: 'background 200ms ease-out',
      }}
    >
      {style.glyph}
    </div>
  );
}
